/**
 * Row buttons that show on hover (job page v2, slice 3): a few icon buttons
 * on the right of a device or file row, a ⋯ for the rest, and the same menu
 * on right-click. The icons appear when the pointer is on the row or the
 * keyboard is inside it; on a touch screen the row's own links still work.
 *
 * Every item comes from the job-actions list (`deviceRowActions` /
 * `fileRowActions`), so ⋯, right-click and ⌘K say and do the same thing.
 */
import type { ReactNode } from "react";
import { MoreHorizontal } from "lucide-react";
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from "@/components/ui/context-menu";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { JobAction } from "../jobActions";
import { JobActionMenuItems } from "../job-action-menu";
import { FOCUS } from "./calm-ui";

/** The hover strip. Hidden until the row is hovered or has focus inside. */
export function RowTools({ label, children }: { label: string; children: ReactNode }) {
    return (
        <span
            role="toolbar"
            aria-label={label}
            className="invisible absolute right-6 top-1.5 z-10 flex items-center gap-0.5 rounded-md border border-border bg-card px-0.5 shadow-sm group-hover/row:visible group-focus-within/row:visible"
        >
            {children}
        </span>
    );
}

export function RowIconButton({
    title,
    onClick,
    children,
    danger,
    disabled,
    testId,
}: {
    title: string;
    onClick: () => void;
    children: ReactNode;
    danger?: boolean;
    disabled?: boolean;
    testId?: string;
}) {
    return (
        <button
            type="button"
            title={title}
            aria-label={title}
            disabled={disabled}
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
            className={cn(
                "inline-flex h-7 w-7 items-center justify-center rounded text-foreground/80 hover:bg-muted disabled:cursor-not-allowed disabled:text-muted-foreground",
                danger && "text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10",
                FOCUS,
            )}
            data-testid={testId}
        >
            {children}
        </button>
    );
}

/** ⋯ with the row's whole menu. */
export function RowMoreMenu({ actions, label, testId }: { actions: JobAction[]; label: string; testId?: string }) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    title="More"
                    aria-label={label}
                    onClick={(e) => e.stopPropagation()}
                    className={cn(
                        "inline-flex h-7 w-7 items-center justify-center rounded text-foreground/80 hover:bg-muted",
                        FOCUS,
                    )}
                    data-testid={testId}
                >
                    <MoreHorizontal className="h-4 w-4" aria-hidden />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
                <JobActionMenuItems actions={actions} showUnavailable testIdPrefix="row-action" />
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/** Right-click anywhere on `children` opens the row's menu. */
export function RowContextMenu({
    actions,
    children,
    testId,
}: {
    actions: JobAction[];
    children: ReactNode;
    testId?: string;
}) {
    return (
        <ContextMenu>
            <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
            <ContextMenuContent className="w-64" data-testid={testId}>
                <JobActionMenuItems actions={actions} showUnavailable kind="context" testIdPrefix="row-context" />
            </ContextMenuContent>
        </ContextMenu>
    );
}
